<template>
	<v-card class="comment-form-container elevation-0">
		<v-form ref="form" v-model="valid" lazy-validation @submit.prevent="validateAndSubmit">
			<div class="comment-form__content d-flex justify-space-between align-center">
				<router-link :to="{ name: 'my-account' }" class="comment-form__avatar-link">
					<Avatar
						class="comment-form__avatar-form mr-2"
						:size="40"
						:avatar="this.$props.avatar"
					/>
				</router-link>

				<div class="comment-form__input">
					<div
						ref="editableDiv"
						class="comment-form__editable"
						:class="
							fields.content && fields.content.trim()
								? 'comment-form__editable--active'
								: ''
						"
						contenteditable="true"
						data-placeholder="Write a comment ..."
						@input="onEditableInput"
					></div>
				</div>

				<button
					class="comment-button"
					type="submit"
					:disabled="loading || isSubmitDisabled"
					:style="{
						backgroundColor: isSubmitDisabled ? '#9b9b9b' : 'var(--primary_color)',
					}"
				>
					<v-progress-circular
						v-if="commentLoading"
						color="#FF5733"
					></v-progress-circular>
					<span v-else>Send</span>
				</button>
			</div>

			<p v-for="(error, index) in errors" :key="index" class="red--text">
				{{ error }}
			</p>
		</v-form>

		<CommentMentionBar
			v-if="isMentionActive"
			:searchString="activeMentionSearch"
			:mentions="mentions"
			@setMention="setMention"
		/>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Form from '@/mixins/Form'
import { ruleRequired } from '@/utils/rules'
import Avatar from '@/components/Avatar'
import CommentMentionBar from '@/components/CommentMentionBar.vue'
import Dwh from '@/mixins/Dwh'

export default {
	name: 'CommentForm',

	components: {
		Avatar,
		CommentMentionBar,
	},

	mixins: [Form, Dwh],

	props: {
		isReply: {
			type: Boolean,
			default: false,
		},
		parent: {
			type: Object,
			default: () => ({}),
		},
		isEdit: {
			type: Boolean,
			default: false,
		},
		comment: {
			type: Object,
			default: () => ({}),
		},
		avatar: {
			type: String,
			default: '',
		},
		isForum: {
			type: Boolean,
			default: false,
		},
	},

	data: () => ({
		fields: {
			content: '',
		},
		mentions: [],
	}),

	computed: {
		...mapGetters('auth', ['isAuthenticated', 'profile']),
		...mapGetters('post', ['isCommentAdding']),
		...mapGetters('forum', ['isForumCommentAdding']),
		activeMentionSearch() {
			const range = this.getActiveMentionRange()
			return range ? range.searchText : ''
		},

		commentLoading() {
			return this.isForum ? this.isForumCommentAdding : this.isCommentAdding
		},

		isSubmitDisabled() {
			return !this.fields.content || !this.fields.content.trim()
		},

		isMentionActive() {
			if (!this.fields.content || this.isCommentAdding || this.isForumCommentAdding) {
				return false
			}

			return !!this.getActiveMentionRange()
		},

		rules() {
			return {
				content: [
					(v) => ruleRequired(v) || 'Field is required.',
					(v) => (typeof v === 'string' && v.trim().length > 0) || 'Text cannot be empty',
				],
			}
		},
	},

	watch: {
		'fields.content': {
			handler(newVal) {
				if (!newVal) {
					this.mentions = []
					return
				}

				this.mentions = this.mentions.filter((mention) => {
					const normalizedUsername = mention.visible_username.replace(/\s+/g, '')

					const regex = new RegExp(
						`(^|\\s)@${this.escapeRegex(normalizedUsername)}(?=\\s|$)`,
						'g'
					)

					return regex.test(newVal)
				})
			},
		},
	},

	mounted() {
		this.syncDivFromContent()
	},

	methods: {
		...mapActions('post', { postAddComment: 'addComment' }),
		...mapActions('forum', { forumAddComment: 'addComment' }),
		getActiveMentionRange() {
			const content = this.fields.content || ''

			const match = content.match(/(^|\s)@([^\s@]*)$/)

			if (!match) return null

			const prefixLength = match[1].length
			const atIndex = match.index + prefixLength

			return {
				atIndex,
				searchText: match[2],
			}
		},

		onEditableInput(e) {
			const text = e.target.innerText.replace(/\n/g, '').trim()

			this.fields.content = text ? e.target.innerText : ''
			if (!text) {
				e.target.innerHTML = ''
			}
		},

		setMention(user) {
			const activeMention = this.getActiveMentionRange()

			if (!activeMention) return

			const normalizedUsername = user.visible_username.replace(/\s+/g, '')

			const before = this.fields.content.slice(0, activeMention.atIndex)

			this.fields.content = `${before}@${normalizedUsername} `

			this.mentions.push(user)

			this.$nextTick(() => {
				this.syncDivFromContent()
				this.setCursorToEnd(this.$refs.editableDiv)
			})
		},

		submitForm() {
			if (this.isSubmitDisabled) {
				return
			}

			let result = this.fields.content

			this.mentions.forEach((item) => {
				const normalizedUsername = item.visible_username.replace(/\s+/g, '')

				const regex = new RegExp(
					`(^|\\s)@${this.escapeRegex(normalizedUsername)}(?=\\s|$)`,
					'g'
				)

				result = result.replace(regex, `$1@[${item.user_id}|${item.visible_username}]`)
			})

			const payload = {
				thread_id: this.parent.thread_id,
				reply_text: result,
			}

			const request = this.isForum
				? this.forumAddComment(payload)
				: this.postAddComment({
						commentBody: payload,
						thread_title: this.parent.thread_title,
				  })

			return request.then(() => {
				this.fields.content = ''
				this.mentions = []

				if (this.$refs.editableDiv) {
					this.$refs.editableDiv.innerHTML = ''
				}

				this.$nextTick(() => {
					this.syncDivFromContent()
				})
			})
		},

		renderHTML(text) {
			if (!text) return ''

			const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

			let result = escaped

			this.mentions.forEach((mention) => {
				const normalizedUsername = mention.visible_username.replace(/\s+/g, '')
				const regex = new RegExp(
					`(^|\\s)@(${this.escapeRegex(normalizedUsername)})(?=\\s|$|&)`,
					'g'
				)

				result = result.replace(
					regex,
					`$1<span class="mention-chip" contenteditable="false">@$2</span>`
				)
			})

			return result
		},

		escapeRegex(str) {
			return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
		},

		syncDivFromContent() {
			const div = this.$refs.editableDiv

			if (!div) return

			div.innerHTML = this.renderHTML(this.fields.content)
		},

		setCursorToEnd(el) {
			if (!el) return

			const range = document.createRange()
			const sel = window.getSelection()

			range.selectNodeContents(el)
			range.collapse(false)

			sel.removeAllRanges()
			sel.addRange(range)

			el.focus()
		},

		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const { user_id: currentUserId } = this.profile

			return userId === currentUserId
		},
	},
}
</script>

<style scoped lang="scss">
.comment-form-container {
	padding: 8px 12px;
	border-bottom: 1.5px solid #e2e2e2 !important;
	position: relative;
}

.comment-form__content {
	width: 100%;
	gap: 8px;
}

.comment-form__avatar-form {
	border-radius: 8px;
}

.comment-form__input {
	width: 100%;
	display: flex;
	align-items: center;
}

.comment-form__editable {
	width: 100%;
	min-height: 48px;
	max-height: 120px;
	overflow-y: auto;
	padding: 12px;
	border-radius: 8px;
	background: #ffffff;
	outline: none;
	font-family: Inter, sans-serif;
	font-weight: 500;
	font-size: 16px;
	line-height: 140%;
	color: #4a4a4a;
	white-space: pre-wrap;
	word-break: break-all;
	cursor: text;
	transition: border-color 0.2s ease;

	&:empty::before {
		content: attr(data-placeholder);
		color: #9b9b9b;
		pointer-events: none;
		font-weight: 400;
	}
}

.comment-button {
	max-width: 134px;
	width: 100%;
	height: 46px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-family: 'Inter', sans-serif;
	font-weight: 700;
	font-size: 18px;
	line-height: 100%;
	white-space: nowrap;

	&:disabled {
		cursor: not-allowed;
		opacity: 0.7;
	}
}

.mention-chip {
	display: inline-block;
	color: var(--primary_color);
	border-radius: 4px;
	font-weight: 500;
	font-size: inherit;
	line-height: inherit;
	cursor: default;
	user-select: none;
}

.comment-form-container .mention-bar {
	position: absolute;
	bottom: 100%;
	z-index: 10;
}

@media screen and (max-width: 400px) {
	.comment-form__content {
		gap: 6px;
	}

	.comment-button {
		max-width: 90px;
		font-size: 16px;
	}
}
</style>
